import { DataTable, useColumns } from "@/components/data-table";
import { DataFetcher } from "@/components/display";
import { useFilters } from "@/components/filters";
import { SchemaFormHook } from "@/components/form";
import { ListPage } from "@/components/list-page";
import { ConfirmDialog, Dialog, ModalHeader, ModalValueExtractor, useOpenModal } from "@/components/modal";
import useHasPermisos from "@/features/auth/hooks/useHasPermisos";
import { getAllClientes } from "@/features/auxiliar/clientes/api/get-all-clientes";
import { getCliente } from "@/features/auxiliar/clientes/api/get-cliente";
import { getAllVendedores } from "@/features/auxiliar/vendedores/api/get-all-vendedores";
import { getVendedor } from "@/features/auxiliar/vendedores/api/get-vendedor";
import { usePayVentaMutation } from "@/features/cajas/caja-actual/api/pay-venta";
import { usePayVentaConFEMutation } from "@/features/cajas/caja-actual/api/create-and-pay-venta-fe";
import CajaPagoForm, { cajaPagoFormSchema } from "@/features/cajas/caja-actual/components/caja-pago-form";
import { useCajaContext } from "@/features/cajas/caja-actual/contexts/caja-context";
import { getAllArticulos } from "@/features/inventario/articulos/api/get-all-articulos";
import { getArticulo } from "@/features/inventario/articulos/api/get-articulo";
import { CancelVentaMutation } from "@/features/inventario/ventas/api/cancel-venta";
import { CancelVentaConFEMutation } from "@/features/inventario/ventas/api/cancel-venta-con-fe";
import { useGetAllUnpaidVentasQuery } from "@/features/inventario/ventas/api/get-all-unpaid-ventas";
import { GetUnpaidVentaQuery } from "@/features/inventario/ventas/api/get-unpaid-venta";
import VentaPrintForm from "@/features/inventario/ventas/components/venta-print-form";
import VentaView from "@/features/inventario/ventas/components/venta-view";
import { useOperationalMode } from "@/features/configuration/hooks/use-operational-mode";
import { OperationalMode } from "@/shared/types/operational-mode";
import { useEmisorConfig } from "@/features/facturacion-electronica/hooks/use-emisor-config";
import { openPdfFromBase64 } from "@/features/facturacion-electronica/utils/pdf-utils";
import Monetizacion from "@/features/monetizacion/components/monetizacion";
import useMonetizacion from "@/features/monetizacion/hooks/use-monetizacion";
import { buildPayVentaFERequest } from "@/features/cajas/caja-actual/utils/build-pay-venta-fe-request";
import { formatDate, formatNumber, formatString } from "@/shared/formats";
import { notify } from "@/shared/notification";
import { Permiso } from "@/shared/types/permiso";
import { Venta } from "@/shared/types/venta";
import CancelIcon from '@mui/icons-material/Cancel';
import PaymentsIcon from '@mui/icons-material/Payments';
import { EmisorConfigRequiredDialog, useCheckEmisorConfig } from "@/features/facturacion-electronica/components/config-required-alert";

export default function PagosPage() {
    const { cajaData } = useCajaContext();

    if (!cajaData) return <CajaUnassigned />
    else if (!cajaData.abierto) return <CajaClosed />
    else return <CajaOpened />
}

function CajaUnassigned() {
    return <ListPage>
        <ListPage.Header>
            <ListPage.Header.Actions title="Pagos Pendientes" />
        </ListPage.Header>
        <DataTable flex={1}>
            <DataTable.Content
                fit="flex"
                emptyText="No hay ninguna caja asignada a este dispositivo."
            />
        </DataTable>
    </ListPage>
}

function CajaClosed() {
    return <ListPage>
        <ListPage.Header>
            <ListPage.Header.Actions title="Pagos Pendientes" />
        </ListPage.Header>
        <DataTable flex={1}>
            <DataTable.Content
                fit="flex"
                emptyText="Debes abrir la caja para poder ver las ventas pendientes."
            />
        </DataTable>
    </ListPage>
}

function CajaOpened() {
    const openModal = useOpenModal();
    const { monedaBase } = useMonetizacion();
    const hasWritePermiso = useHasPermisos(Permiso.WriteVentas);
    const hasPayPermiso = useHasPermisos(Permiso.PagarVenta);

    const filterData = useFilters({
        filters: [
            { type: "dateRange", label: "Fecha", fromName: "desde", toName: "hasta" },
            { type: "string", label: "N° de Venta", name: "numeroVenta" },
            { type: "string", label: "N° de Comprobante", name: "numComprobante" },
            { type: "model", label: "Cliente", name: "clienteId", get: getCliente, getAll: getAllClientes },
            { type: "model", label: "Vendedor", name: "vendedorId", get: getVendedor, getAll: getAllVendedores },
            { type: "model", label: "Artículo", name: "articuloId", get: getArticulo, getAll: getAllArticulos },
            { type: "pagination" }
        ]
    });

    const fetchData = useGetAllUnpaidVentasQuery(filterData.filterData);

    const columnData = useColumns<Venta>({
        columns: [
            { key: "numVenta", header: "N°", render: row => formatString(row.numeroVenta), width: "auto" },
            { key: "fecha", header: "Fecha", render: row => formatDate(row.fecha), width: "auto" },
            { key: "numComprobante", header: "N° de Comprobante", render: row => formatString(row.numComprobante), show: false },
            { key: "cliente", header: "Cliente", render: row => formatString(row.clienteNombre) },
            { key: "vendedor", header: "Vendedor", render: row => formatString(row.vendedorNombre) },
            { key: "condicion", header: "Condición", render: row => row.credito ? "Crédito" : "Contado", width: "auto", align: "center" },
            { key: "tieneDE", header: "Tiene DE", render: row => formatString(row.tieneDE ? "Sí" : "No"), align: "right", width: "auto" },
            { key: "total", header: `Total (${monedaBase.simbolo})`, render: row => formatNumber(row.total), align: "right", width: "auto" },
            { key: "creadoPor", header: "Creado Por", render: row => formatString(row.creadoPor), show: false },
            { key: "creacion", header: "Fecha de Creación", render: row => formatDate(row.fechaCreacion), align: "center", show: false },
            { key: "modificadoPor", header: "Modificado Por", render: row => formatString(row.modificadoPor), show: false },
            { key: "modificacion", header: "Última Modificación", render: row => formatDate(row.fechaModificacion), align: "center", show: false }
        ],
        key: "ventas"
    });

    return <ListPage>
        <ListPage.Header>
            <ListPage.Header.Actions title="Pagos Pendientes">
                <ListPage.Header.Actions.ColumnButton columnData={columnData} />
                <ListPage.Header.Actions.FilterButton />
            </ListPage.Header.Actions>
            <ListPage.Header.Filters filterData={filterData} />
        </ListPage.Header>

        <DataTable flex={1}>
            <DataTable.Content
                rows={fetchData.data?.result}
                columns={[
                    ...columnData.columns,
                    {
                        header: "Acción", width: "auto", align: "center", render: row => <DataTable.EntryOption>
                            <DataTable.EntryOption.ViewButton onClick={() => openModal("view", row.id)} />
                            {hasPayPermiso && <DataTable.EntryOption.Button Icon={PaymentsIcon} title="Pagar" onClick={() => openModal("pagarVenta", row.id)} />}
                            {hasWritePermiso && <DataTable.EntryOption.Button Icon={CancelIcon} title="Anular" onClick={() => openModal("cancel", row)} />}
                        </DataTable.EntryOption>
                    }
                ]}
                fit="flex"
                loading={fetchData.isFetching}
                error={fetchData.error}
            />
            <DataTable.Pagination filterData={filterData} queryResult={fetchData} />
        </DataTable>

        <Dialog code="view" size="md" fullHeight>
            <ModalHeader>Ver Venta</ModalHeader>
            <ModalValueExtractor>
                {ventaId => <GetUnpaidVentaQuery id={ventaId}>
                    {query => <DataFetcher query={query} flex={1}>
                        {venta => <VentaView venta={venta} onPrintButtonClick={() => openModal("printVenta", venta)} />}
                    </DataFetcher>}
                </GetUnpaidVentaQuery>}
            </ModalValueExtractor>
        </Dialog>

        <Dialog code="pagarVenta" size="md" fullHeight>
            <ModalHeader>Pagar Venta</ModalHeader>

            <ModalValueExtractor>
                {ventaId => <GetUnpaidVentaQuery id={ventaId}>
                    {query => <DataFetcher query={query} flex={1}>
                        {venta => <PayVentaDialog venta={venta} />}
                    </DataFetcher>}
                </GetUnpaidVentaQuery>}
            </ModalValueExtractor>
        </Dialog>

        <Dialog code="printVenta" size="xs">
            <ModalValueExtractor>
                {value => <>
                    <ModalHeader>Imprimir Venta N°{(value.venta || value).numeroVenta}</ModalHeader>
                    <VentaPrintForm venta={value.venta || value} ticketOnly={value.ticketOnly} padding={3} />
                </>}
            </ModalValueExtractor>
        </Dialog>

        <ConfirmDialog code="cancel">
            <ModalValueExtractor>
                {venta => venta.tieneDE && venta.credito ? (
                    <CancelVentaConFEMutation id={venta.id}>
                        {mutation => <ConfirmDialog.Content
                            title={`Anular Venta N°${venta.numeroVenta}`}
                            description={`¿Estás seguro que quieres anular la venta N°${venta.numeroVenta}?, si la fecha de emision es mayor a 48 horas se generará una Nota de Crédito Electrónica.`}
                            onConfirm={async () => {
                                const result = await mutation.mutateAsync({});
                                const nce = result.nceGenerada;

                                if (nce?.kudeBase64) {
                                    openPdfFromBase64(nce.kudeBase64);
                                    notify(`La venta N°${venta.numeroVenta} se ha anulado correctamente. Se ha generado una Nota de Crédito Electrónica N°${nce.numeroDE}.`, { variant: "success" });
                                } else {
                                    notify(`La venta N°${venta.numeroVenta} se ha anulado correctamente. La factura electrónica ha sido cancelada/inutilizada.`, { variant: "success" });
                                }
                            }}
                        />}
                    </CancelVentaConFEMutation>
                ) : (
                    <CancelVentaMutation id={venta.id}>
                        {mutation => <ConfirmDialog.Content
                            title={`Anular Venta N°${venta.numeroVenta}`}
                            description={`¿Estás seguro que quieres anular la venta N°${venta.numeroVenta}?`}
                            onConfirm={async () => {
                                await mutation.mutateAsync({});
                                notify(`La venta N°${venta.numeroVenta} se ha anulado correctamente.`, { variant: "success" });
                            }}
                        />}
                    </CancelVentaMutation>
                )}
            </ModalValueExtractor>
        </ConfirmDialog>
    </ListPage>
}

interface PayVentaDialogProps {
    venta: Venta;
}

function PayVentaDialog({ venta }: PayVentaDialogProps) {
    const openModal = useOpenModal();
    const { monedaBase, transferenciaMetodoBase, transferenciaMetodos } = useMonetizacion();
    const { config } = useEmisorConfig();
    const { facturacionEnabled, mode } = useOperationalMode();

    const payVentaNormalMutation = usePayVentaMutation(venta.id);
    const payVentaFEMutation = usePayVentaConFEMutation(venta.id);

    const dEst = config?.establecimiento;
    const dPunExp = config?.puntoExpedicion;

    const checkEmisorConfig = useCheckEmisorConfig();

    return <Monetizacion cotizacion={venta.cotizacion}>
        <EmisorConfigRequiredDialog />
        <SchemaFormHook
            schema={cajaPagoFormSchema}
            defaultValues={{
                pago: { items: [{ transferenciaMetodoId: transferenciaMetodoBase.id, monto: { items: [{ monedaId: monedaBase.id, cantidad: venta.total }] } }], facturacionElectronica: facturacionEnabled && !venta.credito },
                vuelto: { items: [{ transferenciaMetodoId: transferenciaMetodoBase.id, monto: { items: [{ monedaId: monedaBase.id, cantidad: 0 }] } }] }
            }}
            onSubmit={async ({ value }) => {
                if (value.pago.facturacionElectronica && facturacionEnabled && !venta.credito) {
                    checkEmisorConfig(config);

                    const requestData = buildPayVentaFERequest({
                        pagoRequest: { pago: value.pago, vuelto: value.vuelto },
                        monedaBase,
                        transferenciaMetodos,
                        dEst: dEst!,
                        dPunExp: dPunExp!,
                        dTiCam: value.pago.conversionGuarani,
                    });

                    const pago = await payVentaFEMutation.mutateAsync(requestData);
                    const fe = pago?.data?.facturaElectronica;

                    if (fe?.kudeBase64) {
                        openPdfFromBase64(fe.kudeBase64);
                    }

                    notify(pago?.message || `Se ha pagado la venta N°${venta.numeroVenta} correctamente.`, { variant: "success" });
                    openModal("close");
                } else {
                    // Pagar normal
                    const pagoRequest = {
                        pago: value.pago,
                        vuelto: value.vuelto,
                        saltarFE: mode === OperationalMode.GestionConFacturacion
                    };
                    const pago = await payVentaNormalMutation.mutateAsync(pagoRequest);
                    notify(`Se ha pagado la venta N°${venta.numeroVenta} correctamente.`, { variant: "success" });
                    openModal("printVenta", { venta: pago.venta });
                }
            }}
            submitOnce
            closeModalOnSubmit
            preventCloseModalWhileSubmitting={false}
        >
            {form => <CajaPagoForm flex={1} padding={3} venta={venta} form={form} />}
        </SchemaFormHook>
    </Monetizacion>
}